import {FlowStateController} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStateController";
import {FlowStatePresentation} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStatePresentation";
import {DasUBoot} from "../DasUBoot";
import {gsap} from "gsap";
import {SimpleReelModelEventData, SimpleReelModelState} from "../../core/reel/simplereel/SimpleReelModel";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {ArrayUtils} from "../utils/ArrayUtils";
import {DasUBootSound} from "../sound/DasUBootSound";
import {DasUBootStatics, VignetteTint} from "../utils/DasUBootStatics";
import {SymbolUtils} from "../utils/SymbolUtils";
import {isPeriscopeSymbol, PeriscopeSymbol} from "../reels/symbols/PeriscopeSymbol";
import {isTorpedoSymbol} from "../reels/symbols/TorpedoSymbol";
import {DasUBootSymbol, DasUBootSymbolStates} from "../reels/DasUBootSymbol";
import {DasUBootRoundType} from "../server/DasUBootData";
import {DasUBootGameMode} from "../server/DasUBootGameMode";
import {MathUtils} from "../utils/MathUtils";
import {xWaysSplitAndResize} from "../runnerpresentations/xWaysSplitAndResize";
import {ReelUtils} from "../utils/ReelUtils";


interface StopAnimData {
    anim:gsap.core.Timeline,
    prolongSpin:number,
    delayNextStop:number,
}

/**
 * Created by Jerker Nord on 2021-01-28.
 */

export class NormalSpinReelStop implements FlowStatePresentation {
    private _tl:gsap.core.Timeline;
    private _stoppedReels:boolean[];
    private _bouncedReels:boolean[];

    private _nearWins:(gsap.core.Timeline | undefined)[] = [];

    private xWaysSplitPresentation:xWaysSplitAndResize;
    private _reelStopAnimation:gsap.core.Timeline[];

    private _periscopeAnimation:gsap.core.Timeline | undefined;
    private allReelsStopped:boolean;
    private _tlComplete:boolean;

    constructor() {
        this.xWaysSplitPresentation = new xWaysSplitAndResize();
    }

    private skip():void {
        const currentTime = this._tl.time();

        for (let anim of this._nearWins) {
            if (anim) {
                this._tl.remove(anim);
                if (anim.isActive()) {
                    anim.progress(1);
                }
                anim.kill();
            }
        }
        this._nearWins = [];


        const stopsToStagger = [];
        if (DasUBoot.model.data.roundType == DasUBootRoundType.FREE_SPIN) {
            for (let i:number = 2; i < this._reelStopAnimation.length; i++) {
                const childStartTime = this._reelStopAnimation[i].startTime();
                if (childStartTime >= currentTime) {
                    this._tl.remove(this._reelStopAnimation[i]);
                    stopsToStagger.push(this._reelStopAnimation[i].pause(0));
                }
            }
        } else {
            //Stagger all not started,
            for (let i:number = 0; i < this._reelStopAnimation.length; i++) {
                const childStartTime = this._reelStopAnimation[i].startTime();
                if (childStartTime >= currentTime) {
                    this._tl.remove(this._reelStopAnimation[i]);
                    stopsToStagger.push(this._reelStopAnimation[i].pause(0));
                }
            }
        }

        const stagger = DasUBootStatics.reelStopDelayQuickStop - this._reelStopAnimation[0].duration();
        for (let anim of stopsToStagger) {
            let stopTime = this._tl.duration() + stagger;
            if (stopTime < currentTime){
                stopTime = currentTime;
            }

            this._tl.add(anim.play(0), stopTime);
        }
    }

    private createAllAnimations(flowStateController:FlowStateController) {

        this._reelStopAnimation = [];
        for (let i:number = 0; i < DasUBoot.model.clientData.reels.length; i++) {
            const reelData = DasUBoot.model.clientData.reels[i];
            const stopAnim = DasUBoot.reelView.reelModels[i].stop(reelData).pause(0);
            //console.log(stopAnim.duration());

            this._reelStopAnimation.push(stopAnim);
            DasUBoot.reelView.reelModels[i].events?.once(SimpleReelModelState.STOPPED, (data:SimpleReelModelEventData) => this.onReelStop(data, flowStateController, gsap.timeline()));
            DasUBoot.reelView.reelModels[i].events?.once(SimpleReelModelState.BOUNCE, (data:SimpleReelModelEventData) => this.onReelBounce(data));


            const nearWin:StopAnimData = this.getNearWin(i, 0);
            if (nearWin.anim.totalDuration() > 0) {
                this._nearWins.push(nearWin.anim);
            } else {
                this._nearWins.push(undefined);
            }
        }

        this.addTorpedoPartsToSymbols(1);
        this.addTorpedoPartsToSymbols(2);
        this.addTorpedoPartsToSymbols(3);
        this.addTorpedoPartsToSymbols(4);


        const xWay1 = this.xWaysSplitPresentation.getXWaySplit(1);
        if (xWay1.duration() > 0) {
            xWay1.pause(0);
            this._reelStopAnimation[1].add(xWay1.play(0));
        }
        const xWay4 = this.xWaysSplitPresentation.getXWaySplit(4);
        if (xWay4.duration() > 0) {
            xWay4.pause(0);
            this._reelStopAnimation[4].add(xWay4.play(0));
        }

        const periscopeAnimation:gsap.core.Timeline | undefined = this.getNewPeriscope();
        if (periscopeAnimation) {
            this._periscopeAnimation = periscopeAnimation;
            this._reelStopAnimation[2].add(this._periscopeAnimation.play(0));
        }

    }

    private assembleAllAnimations(jurPadding:number = 0) {

        let jurPaddingPerReel = jurPadding * 0.2;

        //Assemble animation
        //let defaultDelay = DasUBoot.apiPlugin.settings.get(APISetting.FAST_SPIN, false) ? DasUBootStatics.reelStopDelayFastSpin : DasUBootStatics.reelStopDelay;
        let value = DasUBoot.apiPlugin.settings.get(APISetting.FAST_SPIN, false) ? DasUBootStatics.reelStopDelayFastSpin : DasUBootStatics.reelStopDelay;
      //  let defaultStagger = defaultDelay - this._reelStopAnimation[0].duration() + jurPaddingPerReel;

        let reelDelays = [
            value * 1.2,
            value * 1.0,
            value * 0.8,
            value * 0.6,
            value * 0.4,
            value * 0.2
        ];

        const firstReelDur = this._reelStopAnimation[0].duration();


        let stagger = 0;
        for (let reel:number = 0; reel < this._reelStopAnimation.length; reel++) {
            let defaultDelay = reelDelays[reel];
            let defaultStagger = defaultDelay - firstReelDur + jurPaddingPerReel;

            const nearWin = this._nearWins[reel];

            if (nearWin) {
                this._tl.add(nearWin.play(0));
                stagger = 0;
            } else if (reel > DasUBootStatics.firstWinHighlightReel && DasUBoot.model.possibleWinData.combinedReels[reel - 1]) {
                //Prolong possible win.
                const delayMultiplier = MathUtils.map(reel, DasUBootStatics.firstWinHighlightReel, 5, 2, 4);
                stagger += defaultDelay * delayMultiplier;
            }


            //Reel stop
            if (nearWin && !this._periscopeAnimation){
                this._tl.add(this._reelStopAnimation[reel].play(0), `>${stagger - this._reelStopAnimation[reel].duration()}`);
            } else {
                this._tl.add(this._reelStopAnimation[reel].play(0), `>${stagger}`);
            }
            stagger = defaultStagger;


            if (reel == 2 && this._periscopeAnimation != undefined) {
                // this._tl.add(this._periscopeAnimation.play(0));
                stagger = 0;
            }
        }
    }

    public start(flowStateController:FlowStateController) {
        this.allReelsStopped = false;
        this._tlComplete = false;
        flowStateController.setSkipCallback(() => this.skip())
        this._stoppedReels = [false, false, false, false, false, false];
        this._bouncedReels = [false, false, false, false, false, false];
        delete this._periscopeAnimation;
        this._nearWins = [];

        this._tl = gsap.timeline({paused:true, onComplete:() => this.presentationComplete(flowStateController)});


        this.createAllAnimations(flowStateController);


        let jurPadding = 0
        if (DasUBoot.model.data.mode == DasUBootGameMode.NORMAL) {
           // jurPadding = DasUBoot.gameFlowController.calculatePaddingDuration(2.4); // 2.4 is default no win duration with 0.04 stepDuration
            jurPadding = DasUBoot.gameFlowController.calculatePaddingDuration(2.0); // 2.4 is default no win duration with 0.04 stepDuration
            //Add half of the jurPadding before stops, and then spread out the rest in between the stops.
            this._tl.add(gsap.to(this, {duration:jurPadding * 0.6}));
            this.assembleAllAnimations(jurPadding * 0.4);

        } else {
            this.assembleAllAnimations(0);
        }

        this._tl.play(0);

    }

    private getNearWin(reelId:number, normalStopDelay:number):StopAnimData {
        let tl:gsap.core.Timeline = gsap.timeline();
        let prolongSpin:number = 0;
        let delayNextStop = 0;

        //Normal near win
        if (DasUBoot.model.gameData.nearWinReels[reelId]) {
            tl = DasUBoot.reelView.reels[reelId].getShowNearWin(6);
            tl.add(() => DasUBoot.backgroundElements.startNearWin(VignetteTint.GREEN), 0);
            tl.add(() => DasUBoot.soundPlayer.startNearWin(), 0.0);
            prolongSpin = tl.totalDuration();

            if (reelId < 5 && !DasUBoot.model.gameData.nearWinReels[reelId + 1]) {
                delayNextStop += normalStopDelay;
            }
        }


        //Periscope near win
        if (reelId == 2 && DasUBoot.model.data.mode == DasUBootGameMode.FREESPIN && DasUBoot.model.periscopeConnect2.length > 0){
            tl = DasUBoot.reelView.reels[reelId].getShowPeriscopeNearWin();
            for (let pos of DasUBoot.model.periscopeConnect2) {
                let symbolData = DasUBoot.reelView.getVisibleSymbolAt(pos.column, pos.row);
                let symbol:DasUBootSymbol = <DasUBootSymbol>symbolData.positionable;
                tl.add(() => symbol.playPeriscopePotentialWin(),0);
            }
            tl.add(() => {DasUBoot.reelView.startPeriscopeNearWin(DasUBootStatics.periscopeNearWinTint)}, 0);
            tl.add(() => DasUBoot.backgroundElements.startNearWin(DasUBootStatics.periscopeNearWinTint), 0);
            tl.add(() => DasUBoot.soundPlayer.startPeriscopeNearWin(), 0);
            prolongSpin = tl.totalDuration();
        }


        return {
            prolongSpin:prolongSpin,
            delayNextStop:delayNextStop,
            anim:tl
        };
    }

    private getNewPeriscope():gsap.core.Timeline | undefined {
        const tl:gsap.core.Timeline = gsap.timeline({paused:true});
        //Periscope animation
        if (DasUBoot.model.gameData.periscope != undefined) {


            tl.add(()=>DasUBoot.soundPlayer.playSound(DasUBootSound.NEAR_WIN_ANTICIPATION_WIN_PERISCOPE));
            tl.add(gsap.to(this, {duration:0.45}));
            tl.add(DasUBoot.reelView.periscopeView.backgroundAnimation(0.45, 0.8));

            if (DasUBoot.model.gameData.periscope!.hit) {
                const startExtension = 7 - DasUBoot.model.gameData.periscope!.nudge;
                const symbolData = DasUBoot.reelView.getVisibleSymbolAt(2, startExtension);
                const symbol = <PeriscopeSymbol>symbolData.positionable;

                if (!isPeriscopeSymbol(symbol)) {
                    DasUBoot.apiPlugin.error.trigger("Symbol is not periscope symbol");
                }
                const periscopeOrigin =  new PIXI.Point(360, DasUBootStatics.symbolPeriscopeHeight * (startExtension-2));


                tl.add(DasUBoot.reelView.periscopeView.getShootAnimation(DasUBoot.model.gameData.periscope!.hitChance, true));

                tl.add(DasUBoot.reelView.reelFrame.xBombMultiplierDisplay.incrementMultiplier(1, periscopeOrigin));

                tl.add(symbol.getStateAnimation(DasUBootSymbolStates.EXPAND));
                tl.add(SymbolUtils.getPeriscopeNudgeAnimation(2, DasUBoot.model.gameData.periscope!.nudge, false))
                tl.add(() => DasUBoot.reelView.reelModels[2].init(["L1", "PW", "PW", "PW", "PW", "PW", "PW", "PW", "PW", "L2"]));

            } else {
                tl.add(DasUBoot.reelView.periscopeView.getShootAnimation(DasUBoot.model.gameData.periscope!.hitChance, false));
            }
        }

        if (tl.duration() > 0){
            return tl;
        }

        return undefined;
    }

    private onReelBounce(data:SimpleReelModelEventData) {
        DasUBoot.reelView.reels[data.reel].getHideNearWin();
        DasUBoot.reelView.reelFrame.possibleWinWaysDisplay.setWinWays(DasUBoot.model.possibleWinWays[data.reel]);
        this.manageStopSound(data);
        this.manageNearWinStop(data);
        this.manageLamps(data);
        this.manageAirPressure(data);
       // this.manageFreeSpinReTrigger(data);
        ReelUtils.highlightWinLine(data.reel);
    }

    private onReelStop(data:SimpleReelModelEventData, flowStateController:FlowStateController, animation:gsap.core.Timeline) {
        if (animation.duration() > 0) {
            animation.play();
            animation.add(() => {
                this._stoppedReels[data.reel] = true;
                if (ArrayUtils.isAllTrue(this._stoppedReels)) {
                    this.allReelsStopped = true;
                    this.completePresentation(flowStateController);
                }
            });
        } else {
            this._stoppedReels[data.reel] = true;
            if (ArrayUtils.isAllTrue(this._stoppedReels)) {
                this.allReelsStopped = true;
                this.completePresentation(flowStateController);
            }
        }
    }

    private presentationComplete(flowStateController:FlowStateController) {
        this._tlComplete = true;
        this.completePresentation(flowStateController)
    }

    private manageAirPressure(data:SimpleReelModelEventData) {
        if (data.reel == 4) {
            gsap.to(this, {duration:0.01, onComplete:()=>DasUBoot.reelView.reelFrame.stopScatterAttention()});
        }
    }
    private manageNearWinStop(data:SimpleReelModelEventData) {
        if (DasUBoot.model.data.mode == DasUBootGameMode.NORMAL) {
            if (data.reel == 4) {
                DasUBoot.soundPlayer.stopNearWin(DasUBoot.model.data.nextMode);
                DasUBoot.backgroundElements.stopNearWin(DasUBoot.model.data.nextMode);
            }
        }

        if (DasUBoot.model.data.mode == DasUBootGameMode.FREESPIN) {
            if (data.reel == 2) {
                DasUBoot.reelView.stopPeriscopeNearWin();
                DasUBoot.soundPlayer.stopPeriscopeNearWin(DasUBoot.model.gameData.periscope?.hasScope);
                DasUBoot.backgroundElements.stopNearWin(DasUBoot.model.data.nextMode);

                for (let pos of DasUBoot.model.periscopeConnect2) {
                    let symbolData = DasUBoot.reelView.getVisibleSymbolAt(pos.column, pos.row);
                    let symbol:DasUBootSymbol = <DasUBootSymbol>symbolData.positionable;
                    symbol.stopPeriscopePotentialWin();
                }
            }
        }
    }

    private manageStopSound(data:SimpleReelModelEventData) {
        this._bouncedReels[data.reel] = true;
        if (ArrayUtils.isAllTrue(this._bouncedReels)) {
            DasUBoot.soundPlayer.stopSound(DasUBootSound.REELS_SPINNING);
        }
        DasUBoot.soundPlayer.playReelStopSound(data.reel);
    }

    private manageLamps(data:SimpleReelModelEventData) {

        //xWay and xNudge
        if (data.reel == 1 && DasUBoot.model.gameData.mystery && DasUBoot.model.gameData.mystery.positions[0] >= 0) {
            DasUBoot.reelView.reelFrame.switchLampOnReel(1, 0);
        }
        if (data.reel == 2 && DasUBoot.model.gameData.xNudges?.doReelNudge[0]) {
            DasUBoot.reelView.reelFrame.switchLampOnReel(2, 0);
        }
        if (data.reel == 3 && DasUBoot.model.gameData.xNudges?.doReelNudge[1]) {
            DasUBoot.reelView.reelFrame.switchLampOnReel(3, 0);
        }
        if (data.reel == 4 && DasUBoot.model.gameData.mystery && DasUBoot.model.gameData.mystery.positions[1] >= 0) {
            DasUBoot.reelView.reelFrame.switchLampOnReel(4, 0);
        }


        //xBomb
        if (DasUBoot.model.clientData.xBomb[data.reel]) {
            DasUBoot.backgroundElements.startXBombAttention();
        }


    }


    private completePresentation(flowStateController:FlowStateController) {
        if (this._tlComplete && this.allReelsStopped) {
            flowStateController.finish();
        }
    }

    private addTorpedoPartsToSymbols(reelID:number) {
        if (reelID == 0 || reelID == 5) return;

        for (let data of DasUBoot.model.clientData.torpedoParts) {
            if (data && data.reel == reelID && data.addedThisSpin != undefined) {

                const landingSymbol = DasUBoot.reelView.getVisibleSymbolAt(data.reel, data.addedThisSpin.position).positionable;
                if (isTorpedoSymbol(landingSymbol)) {
                    landingSymbol.addTorpedoPart(data.landingSize);
                } else {
                    DasUBoot.apiPlugin.error.trigger("Symbol is not torpedo symbol");
                }
            }
        }


    }

}